// Stripe payments are handled via the "firestore-stripe-payments" Firebase extension.
// Checkout sessions and product/price data live in Firestore under the paths below.
// Stripe redirects are done via window.location.assign(url) — this is intentional,
// not a navigation bug. The Stripe hosted checkout page is an external URL.

import {
	addDoc,
	collection,
	doc,
	getDocs,
	onSnapshot,
	query,
	where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from '../config/firebase';
import { ONETIME_PRODUCT_ID, STRIPE_ROLES } from '../config/constants';

// Fetches all active prices for a given product from Firestore.
// Returns an array of { id, data } objects.
export const fetchProductPrices = async (productId) => {
	const queryPrices = query(
		collection(db, `products/${productId}/prices`),
		where('active', '==', true),
	);
	const snapshot = await getDocs(queryPrices);
	return snapshot.docs.map((price) => ({ id: price.id, data: price.data() }));
};

// Creates a Stripe checkout session document in Firestore for a given user.
// The Stripe extension watches for this document and populates the checkout URL.
// Returns the Firestore doc reference (used to observe the session via observeCheckoutSession).
export const createCheckoutSession = (uid, sessionData) =>
	addDoc(collection(db, `customers/${uid}/checkout_sessions`), sessionData);

// Subscribes to a checkout session document. Calls back with { error, url } as
// the Stripe extension populates the session. Returns the unsubscribe function.
export const observeCheckoutSession = (uid, sessionId, callback) =>
	onSnapshot(
		doc(db, `customers/${uid}/checkout_sessions`, sessionId),
		(snapshot) => callback(snapshot.data()),
	);

// Checks whether the user has a succeeded lifetime-access payment in Firestore.
export const checkLifetimeAccess = async (uid) => {
	const q = query(
		collection(db, `customers/${uid}/payments`),
		where('status', '==', 'succeeded'),
	);
	const snapshot = await getDocs(q);
	return snapshot.docs.some((doc) => {
		const items = doc.data().items || [];
		return items.some(
			(item) => item?.price?.product === ONETIME_PRODUCT_ID,
		);
	});
};

// Checks whether the user has an active Stripe subscription via their ID token claims.
export const checkSubscriptionAccess = async (user) => {
	const tokenResult = await user.getIdTokenResult(true);
	return tokenResult.claims.stripeRole === STRIPE_ROLES.PAYING_USER;
};

// Returns the interval ('month' or 'year') of the user's active subscription, or null if none.
export const getSubscriptionInterval = async (uid) => {
	const q = query(
		collection(db, `customers/${uid}/subscriptions`),
		where('status', '==', 'active'),
	);
	const snapshot = await getDocs(q);
	if (snapshot.empty) return null;
	const items = snapshot.docs[0].data().items || [];
	return items[0]?.price?.interval ?? null;
};

// Calls the Stripe extension Cloud Function to get the customer portal URL.
// Returns the URL string, or "error" if the call fails.
export const getPortalUrl = async () => {
	const functions = getFunctions(app);
	const functionRef = httpsCallable(
		functions,
		'ext-firestore-stripe-payments-createPortalLink',
	);
	return functionRef({ returnUrl: window.location.origin })
		.then((result) => result?.data?.url)
		.catch(() => 'error');
};
